import {
  Camera,
  Care,
  DocumentStacked,
  Fitness,
  Hospitality,
  It,
  Ownership,
  RepairTools,
  Scooter,
  Security,
  Stock,
  Wallet,
} from '@insify/design-system/line-icons';
import { ActivitySelector, RichContentWithTooltip, WizardLayoutFormWithCommonSection } from '@insify/funnel-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PrivacyPolicyLink } from '../../components/PrivacyPolicyLink';
import { useCoBranding } from '../../hooks/useCoBranding';
import { useContent } from '../../hooks/useContent';

import styles from './Activity.module.css';
import { useActivityEventHandlers } from './useActivityEventHandlers';
import { useActivityValues } from './useActivityValues';

export const ActivityPage = () => {
  const { onAddActivity, onRemoveActivity, onSearchActivity, onBack, onSubmit, isSubmitting } =
    useActivityEventHandlers();

  const { t } = useTranslation(['common', 'funnel']);
  const { quoteWizardActivity } = useContent() || {};

  const { activities, selected } = useActivityValues();

  const { hasCoBranding } = useCoBranding();

  const [activitySelectorSelection, setActivitySelectorSelection] = useState<string[]>([]);

  const backProps = hasCoBranding ? { isBackHidden: true as const } : { backCopy: t('common:back'), onBack };
  return (
    <WizardLayoutFormWithCommonSection
      isSubmitting={isSubmitting}
      isForwardDisabled={activitySelectorSelection.length === 0}
      forwardCopy={t('common:continue')}
      onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        onSubmit();
      }}
      {...backProps}
    >
      <RichContentWithTooltip content={quoteWizardActivity} className={styles.content} />
      <ActivitySelector
        orderedVerticals={[
          { name: 'Informatique', icon: <It /> },
          { name: 'Conseil, formation & services aux professionnels', icon: <DocumentStacked /> },
          { name: 'Construction et bâtiment', icon: <RepairTools /> },
          { name: 'Transport', icon: <Scooter /> },
          { name: 'Bien-être, santé & développement personnel', icon: <Fitness /> },
          { name: 'Communication, marketing & media', icon: <Camera /> },
          { name: 'Commerce & e-commerce', icon: <Stock /> },
          { name: 'Services à la personne', icon: <Care /> },
          { name: 'Finance, assurance & immobilier', icon: <Wallet /> },
          { name: 'Bars, hotels & restauration', icon: <Hospitality /> },
          { name: 'Sécurité', icon: <Security /> },
          { name: 'Artisans', icon: <Ownership /> },
        ]}
        activities={activities}
        selected={selected?.map(({ insifyActivityCode }) => insifyActivityCode) ?? []}
        translations={{
          searchPlaceholder: t('funnel:activity_search_placeholder'),
          yourActivities: t('funnel:activity_your_activities'),
          verticalHeading: t('funnel:activity_vertical_heading'),
          searchActivities: t('funnel:activity_vertical_modal_close_button'),
          verticalModalHeading: t('funnel:activity_vertical_modal_heading'),
          save: t('funnel:activity_save'),
          details: t('funnel:activity_details'),
          inclusions: t('funnel:activity_inclusions'),
          exclusions: t('funnel:activity_exclusions'),
          selectActivity: t('funnel:activity_save'),
          noResultHeader: t('funnel:activity_no_result_header'),
          noResultCTA: t('common:no_result_cta'),
          noResultPhone: '+33186472770',
        }}
        onAddActivity={onAddActivity}
        onRemoveActivity={onRemoveActivity}
        onSearchActivity={onSearchActivity}
        onSelectionChanged={setActivitySelectorSelection}
      />
      <PrivacyPolicyLink className={styles.privacyLink} />
    </WizardLayoutFormWithCommonSection>
  );
};
